import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faCopy, faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons';

const LogContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  margin-top: var(--space-4);
  border: 1px solid var(--border-color-light);
  box-shadow: var(--shadow-sm);
`;

const LogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-md);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--primary-50);
  }
`;

const LogTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: 600;
  color: var(--text-color);
  font-size: var(--text-base);
  font-family: var(--font-heading-medium);

  .icon {
    color: var(--primary-600);
  }
`;

const LogActions = styled.div`
  display: flex;
  gap: var(--space-2);
`;

const LogButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);

  &:hover {
    background: var(--primary-50);
    color: var(--primary-600);
  }

  &.copied {
    color: var(--success-600);
  }
`;

const LogContent = styled.div`
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: ${props => props.isOpen ? 'auto' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: ${props => props.isOpen ? 'var(--space-4)' : '0'};
`;

const LogSection = styled.div`
  background: var(--neutral-900);
  color: var(--neutral-100);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-4);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  overflow-x: auto;
  border: 1px solid var(--neutral-700);

  h4 {
    color: var(--primary-400);
    margin-bottom: var(--space-2);
    font-weight: 400;
    font-size: var(--text-sm);
    font-family: var(--font-heading-regular);
  }

  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
    line-height: 1.5;
  }
`;

const LogMetadata = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  padding: var(--space-4);
  background: var(--neutral-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color-light);
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  .label {
    font-size: var(--text-xs);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: var(--text-color);
    font-size: var(--text-sm);
  }
`;

function LogViewer({ logData }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!logData) return null;

  const handleCopy = () => {
    const logText = `
=== LOG DATA ===
Timestamp: ${logData.timestamp || new Date().toISOString()}
Agent: ${logData.agent || 'Unknown'}
Action: ${logData.action || 'Unknown'}

=== PROMPT ===
${logData.prompt || 'No prompt data'}

=== RESPONSE ===
${JSON.stringify(logData.response, null, 2)}

=== METADATA ===
Model: ${logData.model || 'Unknown'}
Temperature: ${logData.temperature || 'Unknown'}
Max Tokens: ${logData.maxTokens || 'Unknown'}
${logData.metadata ? JSON.stringify(logData.metadata, null, 2) : ''}
    `;

    navigator.clipboard.writeText(logText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatJSON = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <LogContainer>
      <LogHeader onClick={() => setIsOpen(!isOpen)}>
        <LogTitle>
          <FontAwesomeIcon icon={faCode} className="icon" />
          <span>לוג טכני</span>
        </LogTitle>
        <LogActions>
          <LogButton onClick={(e) => { e.stopPropagation(); handleCopy(); }} className={copied ? 'copied' : ''}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
            {copied ? 'הועתק!' : 'העתק'}
          </LogButton>
          <LogButton onClick={(e) => e.stopPropagation()}>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
          </LogButton>
        </LogActions>
      </LogHeader>

      <LogContent isOpen={isOpen}>
        {logData.metadata && (
          <LogMetadata>
            <MetadataItem>
              <span className="label">מודל</span>
              <span className="value">{logData.model || 'gemini-2.0-flash-exp'}</span>
            </MetadataItem>
            <MetadataItem>
              <span className="label">טמפרטורה</span>
              <span className="value">{logData.temperature || '0.7'}</span>
            </MetadataItem>
            <MetadataItem>
              <span className="label">מקס טוקנים</span>
              <span className="value">{logData.maxTokens || '8192'}</span>
            </MetadataItem>
            {logData.timestamp && (
              <MetadataItem>
                <span className="label">זמן</span>
                <span className="value">{new Date(logData.timestamp).toLocaleTimeString('he-IL')}</span>
              </MetadataItem>
            )}
          </LogMetadata>
        )}

        {logData.prompt && (
          <LogSection>
            <h4>PROMPT SENT:</h4>
            <pre>{logData.prompt}</pre>
          </LogSection>
        )}

        {logData.response && (
          <LogSection>
            <h4>RAW RESPONSE:</h4>
            <pre>{formatJSON(logData.response)}</pre>
          </LogSection>
        )}

        {logData.error && (
          <LogSection style={{ background: '#7f1d1d' }}>
            <h4 style={{ color: '#fca5a5' }}>ERROR:</h4>
            <pre>{logData.error}</pre>
          </LogSection>
        )}

        {logData.additionalData && (
          <LogSection>
            <h4>ADDITIONAL DATA:</h4>
            <pre>{formatJSON(logData.additionalData)}</pre>
          </LogSection>
        )}
      </LogContent>
    </LogContainer>
  );
}

export default LogViewer;
